import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, Phone, ArrowDown } from "lucide-react";
import { EASE } from "./Reveal";
import { PHONE_TEL, PHONE_DISPLAY, waLink } from "@/lib/contact";

const HERO_IMG =
  "https://images.unsplash.com/photo-1758691463620-188ca7c1a04f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHx0ZWxlaGVhbHRoJTIwY29uc3VsdGF0aW9uJTIwbGFwdG9wfGVufDB8fHx8MTc5Mjk1NTExNnww&ixlib=rb-4.1.0&q=85";

const lines = ["Move.", "Heal.", "Live."];

export default function Hero() {
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 800], [0, 110]);
  const textY = useTransform(scrollY, [0, 800], [0, -40]);

  return (
    <section id="top" className="relative overflow-hidden pt-28 md:pt-40 pb-16 md:pb-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-[#8DA37F]" />
            <span className="text-xs tracking-[0.25em] uppercase font-medium text-[#6b7f5e]">
              Online physiotherapy &amp; strength — ESTD 2026
            </span>
          </motion.div>

          <h1
            className="font-display font-light text-[#111111] text-[19vw] sm:text-8xl md:text-[7.5rem] lg:text-[8.5rem] leading-[0.88] tracking-tight"
            data-testid="hero-headline"
          >
            {lines.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "115%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1.1, delay: 0.45 + i * 0.16, ease: EASE }}
                >
                  {i === 1 ? (
                    <span className="italic text-[#8DA37F]">{line}</span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="mt-8 max-w-md text-base md:text-lg leading-relaxed text-[#111111]/70"
          >
            Clinical physiotherapy and athletic strength programming, delivered
            entirely online — expert eyes on your movement, wherever you are.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={waLink("Hi Physiora, I'd like to book a physiotherapy consultation.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="group inline-flex items-center gap-3 bg-[#111111] text-[#F5F0E8] text-xs tracking-[0.15em] uppercase font-medium px-8 py-4 rounded-full hover:bg-[#8DA37F] hover:text-[#111111] hover:-translate-y-0.5 transition-all duration-300"
            >
              <MessageCircle className="h-4 w-4" />
              Book on WhatsApp
            </a>
            <a
              href={PHONE_TEL}
              data-testid="hero-call-btn"
              className="inline-flex items-center gap-3 text-xs tracking-[0.15em] uppercase font-medium px-8 py-4 rounded-full border border-[#111111]/25 text-[#111111] hover:border-[#111111] hover:-translate-y-0.5 transition-all duration-300"
            >
              <Phone className="h-4 w-4" />
              {PHONE_DISPLAY}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="mt-14 hidden md:flex items-center gap-2 text-[#111111]/40 text-xs tracking-[0.2em] uppercase"
          >
            <ArrowDown className="h-4 w-4 animate-float-slow" />
            Scroll to begin
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.7, ease: EASE }}
            className="relative"
          >
            <div className="absolute -bottom-5 -right-5 w-full h-full bg-[#8DA37F]/30" />
            <div className="relative overflow-hidden aspect-[4/5]">
              <motion.img
                src={HERO_IMG}
                alt="Physiotherapist consulting a patient over a laptop video call"
                style={{ y: imgY, scale: 1.15 }}
                className="w-full h-full object-cover object-[68%_center]"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/25 via-transparent to-transparent" />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.4, ease: EASE }}
              className="absolute -left-4 md:-left-10 bottom-8 bg-[#F5F0E8]/85 backdrop-blur-xl border border-white/40 px-5 py-4"
            >
              <p className="font-display text-3xl text-[#111111] leading-none">45<span className="text-lg align-top ml-1">min</span></p>
              <p className="text-xs tracking-[0.15em] uppercase text-[#111111]/60 mt-1">
                Video consultations
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
